import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, AlertOctagon, X } from 'lucide-react';
import { hasVideoFile } from '../../utils/hlsPlayer';
import { AdminPreviewVideo } from './adminShared';

// Admin video preview modal — watch flagged/pending videos before moderating.
export const AdminVideoPreviewModal = ({
  handleApproveVideo,
  handleRejectVideo,
  previewVideo,
  setPreviewVideo,
}) => (
  <div
    className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={() => setPreviewVideo(null)}
    data-testid="video-preview-modal"
  >
    <div
      className="relative w-full max-w-4xl bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-800">
        <div className="min-w-0 pr-4">
          <p className="font-semibold text-white truncate" data-testid="preview-title">
            {previewVideo.title}
          </p>
          <p className="text-sm text-zinc-500 truncate">
            by {previewVideo.artist || previewVideo.username || 'Unknown'}
            {previewVideo.genre && <span className="ml-2 text-zinc-600">· {previewVideo.genre}</span>}
            {previewVideo.status && (
              <span className="ml-2 text-zinc-600">· status: <span className="text-amber-400">{previewVideo.status}</span></span>
            )}
          </p>
        </div>
        <button
          onClick={() => setPreviewVideo(null)}
          className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex-shrink-0"
          title="Close (Esc)"
          data-testid="close-preview"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Video */}
      <div className="bg-black">
        {hasVideoFile(previewVideo) ? (
          <AdminPreviewVideo previewVideo={previewVideo} />
        ) : (
          <div className="px-6 py-16 text-center text-zinc-400">
            <AlertOctagon className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="font-medium">No video file in storage</p>
            <p className="text-sm text-zinc-500 mt-1">
              The upload never completed. There's nothing to preview — delete this row and ask the artist to re-upload.
            </p>
          </div>
        )}
      </div>

      {/* AI flag context (if any) */}
      {previewVideo.ai_flags?.song_check?.is_song === false && (
        <div className="mx-5 my-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 text-xs">
            <p className="font-semibold text-amber-300">
              AI flagged: not a music track ({Math.round((previewVideo.ai_flags.song_check.confidence || 0) * 100)}% confidence)
            </p>
            <p className="text-amber-200/70 mt-0.5">{previewVideo.ai_flags.song_check.reason}</p>
            {previewVideo.ai_flags.song_check.transcript_preview && (
              <p className="text-zinc-500 italic mt-1 line-clamp-3" title={previewVideo.ai_flags.song_check.transcript_preview}>
                "{previewVideo.ai_flags.song_check.transcript_preview}…"
              </p>
            )}
          </div>
        </div>
      )}

      {/* Footer actions — only show approve/reject if this video is still pending */}
      {(previewVideo.status === 'pending_review' || previewVideo.status === 'processing') && (
        <div className="px-5 py-4 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button
            onClick={() => {
              handleRejectVideo(previewVideo.id);
              setPreviewVideo(null);
            }}
            className="px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium text-sm flex items-center gap-2"
            data-testid="preview-reject"
          >
            <XCircle className="w-4 h-4" />
            Reject
          </button>
          <button
            onClick={() => {
              handleApproveVideo(previewVideo.id);
              setPreviewVideo(null);
            }}
            className="px-4 py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium text-sm flex items-center gap-2"
            data-testid="preview-approve"
          >
            <CheckCircle className="w-4 h-4" />
            Approve
          </button>
        </div>
      )}
    </div>
  </div>
);
